import { Component, Inject, ViewChild } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploadFile } from "@angular-ex/uploader";
import { UploaderHtmlTemplate } from "../../../../../shared/components/file-uploader/file-uploader.component";
import { LinkedInImageUploadMaxFileSize } from "../../../../../shared/defaults/file-uploader-max-file-size";
import { ImageEntity } from "../../../../../shared/entities/file/image.entity";
import { LinkedInPostContentType } from "../../../../../shared/enums/campaignPost.enums";
import { BrandCampaignAddContentDialogData } from "../../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaignLinkedInPost } from "../../../../../shared/models/brandCampaignLinkedInPost";
import { AppDataService } from "../../../../../shared/services/api/app-data.service";
import { DialogService } from "../../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../../shared/services/notification.service";
import { CarouselEditComponent } from "../../../brand-campaign-content/campaign-content-form-post-carousel-dialog/carousel-edit/carousel-edit.component";
import { CarouselPreviewComponent } from "../../../brand-campaign-content/campaign-content-form-post-carousel-dialog/carousel-preview/carousel-preview.component";
import { BrandCampaignPostService } from "../../../shared/services/brand-campaign-post.service";
import { LinkedinPostCreationBaseDialogComponent } from "../linkedin-post-creation-base-dialog.component";
import { AnyToImageEntityMapper } from "../../../../../shared/mappers/image.entity.mapper";

export interface LinkedinMultiImagePostCreationDialogData
  extends BrandCampaignAddContentDialogData {
  readonly contentType?: LinkedInPostContentType;
  readonly post?: BrandCampaignLinkedInPost;
}

export type LinkedinMultiImagePostCreationDialogResult = undefined;

@Component({
  selector: "app-linkedin-multi-image-post-creation-dialog",
  templateUrl: "./linkedin-multi-image-post-creation-dialog.component.html",
  styleUrls: [
    "./linkedin-multi-image-post-creation-dialog.component.scss",
    "../../campaign-content-creation-dialog.scss",
  ],
})
export class LinkedinMultiImagePostCreationDialogComponent extends LinkedinPostCreationBaseDialogComponent {
  public uploaderHtmlTemplate = UploaderHtmlTemplate.AddIcon;
  public uploaderSubtitle: string;
  public uploaderUrl: string;
  private imagesToUpload: UploadFile[] = [];
  private uploaderResponsesToHandle: ImageEntity[] = [];
  public readonly MAX_FILE_SIZE = LinkedInImageUploadMaxFileSize;
  @ViewChild("carouselEdition")
  carousel!: CarouselEditComponent;
  @ViewChild("carouselPreview")
  carouselPreview!: CarouselPreviewComponent;
  protected postType = LinkedInPostContentType.MultiImage;
  public readonly fileUploaderResponseMapper = AnyToImageEntityMapper;

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly brandCampaignPostService: BrandCampaignPostService,
    protected readonly notificationService: NotificationService,
    protected readonly dialogService: DialogService,
    protected readonly dialogRef: MatDialogRef<
      LinkedinMultiImagePostCreationDialogComponent,
      LinkedinMultiImagePostCreationDialogResult
    >,
    protected readonly dialog: MatDialog,
    protected readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA) data: LinkedinMultiImagePostCreationDialogData,
  ) {
    super(
      brandCampaignPostService,
      dialogRef,
      dialogService,
      notificationService,
      translateService,
      dialog,
      appDataService,
      data,
    );

    this.uploaderSubtitle =
      this.translateService.instant(
        "brand.campaign.contentCalendarMultiImagePost.validFileExtension",
      ) + " jpg, jpeg, png, mp4";

    this.uploaderUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );
  }

  public canUploadMoreFiles(): boolean {
    return !this.post.images || this.post.images?.length < 9;
  }

  public imageSelected(file: UploadFile): void {
    this.imagesToUpload.push(file);
  }

  public handleUploadCarouselResponse(apiResponse: ImageEntity): void {
    this.uploaderResponsesToHandle.push(apiResponse);
    if (this.imagesToUpload.length > 0) {
      if (this.imagesToUpload.every((file) => file.responseStatus === 200)) {
        this.onImagesUploaded(this.uploaderResponsesToHandle);
      }
    } else {
      this.onImagesUploaded([apiResponse]);
    }
  }

  private onImagesUploaded(apiResponses: ImageEntity[]): void {
    if (!this.post.images) {
      this.post.images = [];
    }
    apiResponses.forEach((response) => {
      this.post.images.push(AnyToImageEntityMapper(response));
    });

    this.imagesToUpload = [];
    this.uploaderResponsesToHandle = [];

    setTimeout(() => {
      this.carouselPreview?.goToEnd();
      this.carousel?.goToEnd();
    }, 500);

    this.checkSaveDisabled();
  }

  public deleteFileConfirm(file: ImageEntity): void {
    this.dialogService.confirm({
      title: "brand.campaign.contentCalendarMultiImagePost.deleteImageTitle",
      message:
        "brand.campaign.contentCalendarMultiImagePost.deleteImageDescription",
      onConfirm: () => this.deletePostFile(file),
    });
  }

  private deletePostFile(file: ImageEntity): void {
    this.subscriptions.add(
      this.brandCampaignPostService
        .deleteCampaignDetailCategoryPostFile(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          file.id,
        )
        .subscribe({
          next: () => {
            this.post.images.forEach((postFile: ImageEntity, index) => {
              if (postFile.id === file.id) {
                this.post.images.splice(index, 1);
                this.carousel.moveBackFromLastSlide(this.post.images, index);
              }
            });
            this.checkSaveDisabled();
            this.notificationService.success(
              "brand.campaign.contentCalendarCarouselPost.imageDelete",
            );
          },
          error: () => {
            this.notificationService.error(
              "brand.campaign.contentCalendarCarouselPost.imageNotDeleted",
            );
          },
        }),
    );
  }

  public textChanged(text: string): void {
    this.post.text = text;
    this.checkSaveDisabled();
  }
  public checkSaveDisabled(): void {
    if (
      !this.post.images ||
      this.post.images.length === 0 ||
      !this.post.text ||
      this.isSaving
    ) {
      this.isSaveDisabled = true;
      return;
    }

    super.checkSaveDisabled();
  }

  public onActionDeleteItem(item: ImageEntity): void {
    this.deleteFileConfirm(item);
  }

  public onThumbnailSelected(index: number): void {
    this.carousel.moveToSlide(index);
  }
}
